import React, { type FC } from "react";
import { type ColumnDef } from "@tanstack/table-core";
import styled from "styled-components";
import {
  ReactTableComponent,
  Typography,
} from "../../../../../components/ui-kit";
import { DateTime } from "luxon";

const StyledReactTableComponent = styled(ReactTableComponent)`
  td {
    height: 45px;
  }
`;

const CopyCell = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  &:hover {
    color: #4f46e5;
  }
`;

const CopyIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, opacity: 0.5 }}
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const handleCopyClick = async (text: string) => {
  try {
    if ("clipboard" in navigator) {
      await navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  } catch (error) {
    console.error("Copy failed", error);
  }
};

const StatusDot = styled.span<{ $active: boolean }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => (p.$active ? "#00bc82" : "#adb5bd")};
  margin-right: 6px;
`;

export const PromotionCodesTable: FC<{
  data: any[];
}> = ({ data }) => {
  const columns = React.useMemo<Array<ColumnDef<any, any>>>(
    () => [
      {
        header: "Code",
        meta: { mobileTitle: true },
        accessorKey: "code",
        cell: (info) => {
          const val = info.getValue() || "-";
          return (
            <CopyCell onClick={() => handleCopyClick(val)}>
              <CopyIcon />
              {val}
            </CopyCell>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "active",
        cell: (info) => (
          <Typography>
            <StatusDot $active={info.getValue()} />
            {info.getValue() ? "Active" : "Inactive"}
          </Typography>
        ),
      },
      {
        header: "Discount",
        id: "discount",
        cell: ({ row: { original } }) => {
          if (original.amount_off) {
            return (
              <Typography>
                £{(original.amount_off / 100).toFixed(2)} off
              </Typography>
            );
          }
          if (original.percent_off) {
            return <Typography>{original.percent_off}% off</Typography>;
          }
          return <Typography>-</Typography>;
        },
      },
      {
        header: "Duration",
        accessorKey: "duration",
        cell: (info) => {
          const val = info.getValue();
          const row = info.row.original;
          if (val === "repeating" && row.duration_in_months) {
            return <Typography>{row.duration_in_months} months</Typography>;
          }
          return (
            <Typography>
              {val ? val.charAt(0).toUpperCase() + val.slice(1) : "-"}
            </Typography>
          );
        },
      },
      {
        header: "Redeemed",
        id: "redeemed",
        cell: ({ row: { original } }) => {
          const max = original.max_redemptions;
          const used = original.times_redeemed ?? 0;
          return (
            <Typography>
              {used}
              {max ? ` / ${max}` : ""}
            </Typography>
          );
        },
      },
      {
        header: "Expires",
        accessorKey: "redeem_by",
        cell: (info) => {
          const val = info.getValue();
          if (!val) return <Typography>-</Typography>;
          const formatted = DateTime.fromSeconds(val).toFormat("dd MMM yyyy");
          return <Typography>{formatted}</Typography>;
        },
      },
    ],
    []
  );

  return (
    <StyledReactTableComponent columns={columns} data={data} padding="0" />
  );
};
